import { StyleSheet, View, useWindowDimensions } from 'react-native';

import { RightRail } from './RightRail';

/**
 * Floating right-side widget rail for desktop web. Uses `position: 'fixed'`
 * so it lives outside the column's render tree entirely — earlier attempts
 * to wrap the column + sidebar in a single row-flex container fought with
 * <Stack>'s internal positioning and blanked the page. Decoupling the
 * sidebar from the layout flow sidesteps that completely.
 *
 * Only renders when the viewport has enough horizontal room to host the
 * sidebar to the right of the centered 880-wide column without overlap.
 * Below that threshold the user sees the column-only layout (no widgets
 * visible at all, by design — they'll come back when the viewport widens).
 */

// Column constants kept in sync with _layout.tsx's APP_MAX_WIDTH-on-desktop.
const COLUMN_MAX = 880;
const SIDEBAR_WIDTH = 280;
const GAP = 24;
// Minimum viewport width where the sidebar fits to the right of the
// centered column without overlapping it: (column + 2*(gap+sidebar)).
// At smaller viewports we hide the rail rather than crowd the column.
const MIN_VIEWPORT = COLUMN_MAX + (GAP + SIDEBAR_WIDTH) * 2;

export function DesktopSidebar() {
  const { width: viewportWidth } = useWindowDimensions();
  if (viewportWidth < MIN_VIEWPORT) return null;

  // The column is centered, so its right edge sits at
  // (viewport + columnWidth) / 2. Sidebar starts GAP px past that.
  const columnRightEdge = (viewportWidth + COLUMN_MAX) / 2;
  const sidebarLeft = Math.round(columnRightEdge + GAP);

  return (
    <View
      pointerEvents="box-none"
      style={[styles.sidebar, { left: sidebarLeft, width: SIDEBAR_WIDTH }]}>
      <RightRail />
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    // 'fixed' pins the rail to the VIEWPORT (Wave 68) — 'absolute'
    // anchored bottom:96 to the page container, so on short windows
    // the rail's box grew taller than the screen and its ScrollView
    // never overflowed → widgets below the fold (House details) were
    // unreachable. Web-only component, so the web-only value is safe.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    position: 'fixed' as any,
    top: 72,
    bottom: 96,
    // Disable the outer's alignItems:center pull on this child.
    alignSelf: 'flex-start',
    // Stack above any other absolutely-positioned UI (the bottom tab
    // bar in particular runs ~10 zIndex on RNW).
    zIndex: 50,
    // No outer chrome — each widget brings its own card background +
    // border. An empty wrapper (no widgets enabled) is invisible.
  },
});
